import { Container, Graphics } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT } from '../config/tokens';
import type { Layout } from '../render/stage';
import { button, iconButton, label, spriteFit, toast } from './components';
import { openModal } from './modal';
import type { Nav } from '../meta/nav';
import { product, type ProductId } from '../monetization/products';
import { sound } from '../audio/sound';
import { haptic } from '../audio/haptics';

/**
 * Buys a product and gives feedback. The button is locked while the store
 * sheet is open (no double purchases, §14). Returns true when bought.
 */
export async function buyWithFeedback(nav: Nav, id: ProductId, feedbackParent: Container, y: number): Promise<boolean> {
  const outcome = await nav.commerce.buy(id);
  if (outcome === 'ok') {
    const p = product(id);
    sound.play('win');
    haptic('success');
    const parts = [p.coins ? `+${p.coins.toLocaleString('en-US')} coins` : '', Object.keys(p.boosters).length ? 'boosters added' : ''].filter(Boolean);
    toast(feedbackParent, `Thank you! ${parts.join(' · ')}`, y, p.coins ? 'ui_coin' : 'booster_hammer');
    return true;
  }
  if (outcome === 'failed') toast(feedbackParent, 'Purchase did not go through. No charge was made.', y);
  return false;
}

/** Raspberry price pill – the price string always comes from the store. */
export function priceButton(text: string, w: number, onTap: () => void, h = 76): Container {
  return button(text, w, 'primary', onTap, h);
}

/** Mockup 33: optional starter pack over the dimmed café. X works immediately. */
export function openStarterOffer(parent: Container, l: Layout | undefined, nav: Nav, onBought: () => void): void {
  if (nav.save().purchases.starter) return;
  const w = 780;
  const h = 1120;
  const m = openModal(parent, l, w, h);
  const close = iconButton('close', () => m.close(), undefined, 80);
  close.position.set(w - 60, 60);
  const title = label('Starter Pack', FONT.size.celebration + 6, { weight: FONT.weight.heading });
  title.position.set(w / 2, 120);
  const sub = label('A cozy little boost', FONT.size.body, { weight: FONT.weight.body });
  sub.position.set(w / 2, 190);
  m.panel.addChild(title, sub);

  const row = (y: number, h2: number) => {
    const g = new Graphics().roundRect(60, y, w - 120, h2, 24).fill(COLORS.latte);
    m.panel.addChild(g);
  };
  // 500 coins
  row(240, 130);
  const coin = spriteFit('ui_coin', 90);
  coin.anchor.set(0.5);
  coin.position.set(190, 305);
  const coins = label('500 Coins', FONT.size.screenTitle, { weight: FONT.weight.heading, anchor: [0, 0.5] });
  coins.position.set(260, 308);
  // one of each booster
  row(390, 250);
  (['booster_hammer', 'booster_spoon', 'booster_moves5'] as const).forEach((f, i) => {
    const b = spriteFit(f, 110);
    b.anchor.set(0.5);
    b.position.set(w / 2 + (i - 1) * 170, 480);
    const x1 = label('×1', FONT.size.body, { weight: FONT.weight.heading });
    x1.position.set(w / 2 + (i - 1) * 170, 565);
    m.panel.addChild(b, x1);
  });
  const each = label('1 of each booster', FONT.size.secondary, { weight: FONT.weight.body });
  each.position.set(w / 2, 612);
  // exclusive bandana
  row(660, 170);
  const band = spriteFit('cosmetic_bandana_paw', 130);
  band.anchor.set(0.5);
  band.position.set(210, 745);
  const ex = label('Exclusive\nPaw Bandana', FONT.size.cardTitle + 2, { weight: FONT.weight.heading, anchor: [0, 0.5] });
  ex.position.set(300, 745);
  m.panel.addChild(coin, coins, each, band, ex);

  const price = nav.commerce.price('starter')?.priceString;
  const buy = priceButton(price ? `Get Pack · ${price}` : 'Not available', 620, () => {
    if (!price || nav.commerce.isBusy) return;
    buy.alpha = 0.6;
    void buyWithFeedback(nav, 'starter', parent, (l?.safeTop ?? 92) + 200).then((ok) => {
      buy.alpha = 1;
      if (ok) {
        m.close();
        onBought();
      }
    });
  }, 106);
  buy.position.set(w / 2, 930);
  const once = label('One-time purchase', FONT.size.secondary, { weight: FONT.weight.body });
  once.position.set(w / 2, 1030);
  m.panel.addChild(buy, once, close);
  gsap.fromTo(band, { rotation: -0.15 }, { rotation: 0.15, duration: 1.2, yoyo: true, repeat: -1, ease: 'sine.inOut' });
}
